import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { SearchInput } from '../../components/SearchInput';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import Paginator from '../../components/Paginator';
import { useTrans } from '../../hooks/useTrans';

export default function AddressesIndex({ addresses, filters }) {
    const translate = useTrans();
    const [form, setForm] = useState({
        query: filters.query || '',
        order_column: filters.orderColumn || 'place',
        order_direction: filters.orderDirection || 'desc',
    });

    function handleSubmit(e) {
        e.preventDefault();
        router.get(window.location.pathname, form, { preserveState: true });
    }

    function handleSort(column) {
        const direction = form.order_column === column && form.order_direction === 'asc' ? 'desc' : 'asc';
        const newForm = { ...form, order_column: column, order_direction: direction };
        setForm(newForm);
        router.get(window.location.pathname, newForm, { preserveState: true });
    }

    function handleDelete(debtorId, addressId) {
        if (window.confirm(translate('Weet je zeker dat je het adres wilt verwijderen?'))) {
            router.delete(`/addresses/verwijderen/${debtorId}/${addressId}`);
        }
    }

    function SortableHead({ column, children, className = '' }) {
        const isActive = form.order_column === column;
        const arrow = isActive ? (form.order_direction === 'asc' ? ' ↑' : ' ↓') : '';
        return (
            <TableHead
                className={`py-5 px-2.5 text-[#003545] font-normal text-sm cursor-pointer select-none hover:text-[#0e8e6a] ${className}`}
                onClick={() => handleSort(column)}
            >
                <span className="underline">{children}</span>{arrow}
            </TableHead>
        );
    }

    const hasAddresses = addresses && addresses.data && addresses.data.length > 0;

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px] pb-0">
                    <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2 mb-6">
                        <h3 className="text-2xl font-bold text-[#003545]">{translate('Adresboek')}</h3>
                        <div className="flex items-center gap-2 ml-auto">
                            <SearchInput
                                name="query"
                                value={form.query}
                                onChange={(e) => setForm({ ...form, query: e.target.value })}
                                onClear={() => {
                                    const newForm = { ...form, query: '' };
                                    setForm(newForm);
                                    router.get(window.location.pathname, newForm, { preserveState: true });
                                }}
                                showClear={!!filters.query}
                                placeholder={translate('Zoeken')}
                                className="w-56"
                            />
                            <Button variant="primary" type="submit">{translate('Zoeken')}</Button>
                            <Button variant="primary" asChild>
                                <Link href="/addresses/toevoegen">{translate('Adres toevoegen')}</Link>
                            </Button>
                        </div>
                    </form>
                </div>

                <div className="px-[30px] pb-[30px]">
                    <Table>
                        <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                            <TableRow>
                                <SortableHead column="company_name">{translate('Bedrijf')}</SortableHead>
                                <SortableHead column="department">{translate('Afdeling')}</SortableHead>
                                <SortableHead column="street">{translate('Straat')}</SortableHead>
                                <SortableHead column="postal_code">{translate('Postcode')}</SortableHead>
                                <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Huisnummer')}</TableHead>
                                <SortableHead column="place">{translate('Plaats')}</SortableHead>
                                <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Laadadres')}</TableHead>
                                <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Losadres')}</TableHead>
                                <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Acties')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {hasAddresses ? (
                                addresses.data.map((address, index) => (
                                    <TableRow key={`${address.debtorId}-${address.id}`} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                        <TableCell className="py-5 px-2.5">
                                            <Link href={`/addresses/${address.debtorId}/${address.id}`} className="underline">
                                                {address.companyName}
                                            </Link>
                                        </TableCell>
                                        <TableCell className="py-5 px-2.5">{address.department}</TableCell>
                                        <TableCell className="py-5 px-2.5">{address.street}</TableCell>
                                        <TableCell className="py-5 px-2.5">{address.postalCode}</TableCell>
                                        <TableCell className="py-5 px-2.5">{address.houseNumber} {address.houseNumberAddition}</TableCell>
                                        <TableCell className="py-5 px-2.5">{address.place}</TableCell>
                                        <TableCell className="py-5 px-2.5">{address.pickup ? '✔' : ''}</TableCell>
                                        <TableCell className="py-5 px-2.5">{address.delivery ? '✔' : ''}</TableCell>
                                        <TableCell className="py-5 px-2.5">
                                            <button
                                                onClick={() => handleDelete(address.debtorId, address.id)}
                                                className="text-[#bbb] hover:text-[#dc3939] transition-all cursor-pointer"
                                                title={translate('Verwijderen')}
                                            >
                                                <svg className="h-5 w-5 fill-current" role="img">
                                                    <use href="#glyph-trash-can"></use>
                                                </svg>
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} className="py-5 px-2.5 text-center text-gray-500">
                                        {translate('Er zijn geen adressen bekend')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <Paginator links={addresses?.links} />
                </div>
            </div>
        </div>
    );
}
